import Head from "next/head";
import { useRouter } from "next/router";
import useSWRInfinite from "swr/infinite";

import { useCallback, useEffect, useMemo, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import InfiniteScroll from "react-swr-infinite-scroll";

import Bugsnag from "@bugsnag/js";
import classNames from "classnames";
import { uniqBy } from "lodash";
import truncate from "lodash/truncate";
import { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";

import AccountUpgradeModal from "components/AccountUpgradeModal";
import { Loader } from "components/customElements/loader";
import AIWorkflowWidget from "components/DashboardPage/DashboardPageInsightList/AIWorkflowWidget";
import DianomiAd from "components/DianomiAd";
import EmptyState from "components/EmptyState";
import { entityUrl } from "components/EntityPageLayout";
import FocusedSwitch from "components/FocusedSwitch";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Paginator from "components/Paginator";
import PublicationSnippet from "components/PublicationSnippet";
import ViewModeToggle from "components/ViewModeToggle";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

import styles from "./style.module.scss";
import { usePrimer } from "./usePrimer";

type EntityPageInsightsListProps = {
  slug?: string;
};

export const entityInsightsUrl = (
  slug: string,
  pageIndex: number,
  filterTxt?: string,
  sortStr?: string,
  mobile?: boolean,
  tab?: string,
  pageSize?: string,
  focused?: boolean
) => {
  const url = new ApiEndpoint(
    `/api/v2/entities/${slug}/${
      focused ? "primary-insights" : "aggregated-insights"
    }`
  );
  url.searchParams.append(
    "include",
    "content-vertical,account,company,primary-entity,latest-comment-thread,latest-comment-thread.commenter,latest-comment-thread.attachment,smart-summary"
  );
  url.searchParams.append("filter[aasm-state]", "published");
  switch (tab) {
    case "insights":
      url.searchParams.append("filter[is-broker]", "false");
      url.searchParams.append("filter[is-ai-generated]", "false");
      url.searchParams.append("filter[is-issuerpaid]", "false");
      url.searchParams.append("filter[is-exchange-sponsored]", "false");
      break;
    case "broker-reports":
      url.searchParams.append("filter[is-broker]", "true");
      break;
    case "sponsored":
      url.searchParams.append("filter[or][is-issuerpaid]", "true");
      url.searchParams.append("filter[or][is-exchange-sponsored]", "true");
      break;
    case "syndicated":
      url.searchParams.append("filter[is-syndicated]", "true");
      break;
    case "podcast":
      url.searchParams.append("filter[is-podcast]", "true");
      break;
    case "buyside":
      url.searchParams.append("filter[is-buyside]", "true");
      break;
    case "primer":
      url.searchParams.append("filter[ai-generated-content-type]", "primer");
      break;
  }
  url.searchParams.append("page[size]", pageSize || "5");
  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append(
    "sort",
    sortStr === "-score,-published-at" && focused
      ? "-published-at"
      : sortStr || (focused ? "-published-at" : "-score,-published-at")
  );
  url.searchParams.append(
    "fields[insights]",
    "content-vertical,account,company,primary-entity,latest-comment-thread,smart-summary" +
      ",slug,tagline,shareable-links,tagline," +
      (mobile ? "" : "byline,executive-summary,boomerang-reason,") +
      "total-combined-read,is-exchange-sponsored,is-broker,is-issuerpaid,is-commissioned,is-ai-generated,ai-generated-content-type,is-boost,is-buyside,is-syndicated,is-podcast," +
      "imperative,published-at,republished-at,conviction,is-embargoed,total-discussions"
  );
  url.searchParams.append("fields[entities]", "slug,pretty-name,security");
  url.searchParams.append(
    "fields[accounts]",
    "slug,name,short-name,avatar-url,title,company-name,summary,areas-of-expertise,is-followed,is-insight-provider,activated"
  );
  url.searchParams.append(
    "fields[companies]",
    "slug,name,avatar-url,publish-under-company-name,title,analysts-count,summary,areas-of-expertise,is-followed"
  );
  url.searchParams.append("fields[content-verticals]", "slug,name");

  if (filterTxt) {
    url.searchParams.append("filter[fulltext]", filterTxt);
  }

  return url.href;
};

export const sortOptions = [
  { id: "relevant", name: "Suggested", sortStr: "-score,-published-at" },
  { id: "recent", name: "Recent", sortStr: "-published-at,-updated-at" },
  {
    id: "viewed",
    name: "Viewed",
    sortStr: "-total-combined-read,-published-at",
  },
  {
    id: "discussed",
    name: "Discussed",
    sortStr: "-total-discussions,-published-at",
  },
];

const EntityPageInsightsList = ({
  slug: slugProp,
}: EntityPageInsightsListProps) => {
  const router = useRouter();
  const slug = slugProp || (router.query.slug as string);
  const tab = router.query.tab as string;
  const page = router.query.page && parseInt(router.query.page as string);

  const { mobile } = useMediaQuery();

  const { authenticated, account } = useAuthentication();

  const [filterTxt, setFilterTxt] = useState("");
  const [filterTxtValue, setFilterTxtValue] = useState("");
  const [filterTxtError, setFilterTxtError] = useState(
    "Please enter at least 3 characters"
  );
  const [focusedOnly, setFocusedOnly] = useState(false);
  const [isCompact, setCompactView] = useState(mobile);
  const [sortOption, setSortOption] = useState(sortOptions[0]);

  const { data: entity } = useApi(entityUrl(slug));

  const swr = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.data?.length === 0) return null;

      return entityInsightsUrl(
        slug,
        page || pageIndex + 1,
        filterTxt,
        sortOption.sortStr,
        mobile,
        tab,
        authenticated && !page ? "12" : "5",
        focusedOnly
      );
    },
    baseFetcher,
    {
      revalidateFirstPage: false,
      revalidateOnMount: false,
      revalidateOnFocus: false,
    }
  );
  const { data, isValidating, mutate, size, setSize } = swr;

  const totalPages = data?.[size - 1]?.meta?.totalPages;
  const hasMore = !page && totalPages > size;
  const isEmpty = !data?.[0]?.data?.length;
  const isRefreshing = isValidating && data && data.length === size;

  const publications = useMemo(() => {
    const items = swr.data?.reduce((acc, page) => {
      if (page?.data) return [...acc, ...page.data];
      return acc;
    }, []);

    return uniqBy(items, (publication: Insight) => publication.id);
  }, [swr.data]);

  const origin = useMemo(() => {
    const endpoint = `entities/${slug}/aggregated-insights`;
    let sort = sortOption || sortOptions?.[0];
    const filters: { [key: string]: string } = {};

    filters["include"] =
      "content-vertical,account,company,primary-entity,latest-comment-thread,latest-comment-thread.commenter";

    filters["fields[insights]"] =
      "content-vertical,account,company,primary-entity,latest-comment-thread" +
      ",slug,tagline,shareable-links,tagline," +
      (mobile ? "" : "byline,executive-summary,boomerang-reason,") +
      "total-combined-read,is-exchange-sponsored,is-broker,is-issuerpaid,is-commissioned,is-boost,is-buyside,is-syndicated,is-podcast," +
      "imperative,published-at,republished-at,conviction,is-embargoed,total-discussions";
    filters["fields[entities]"] = "slug,pretty-name";
    filters["fields[accounts]"] =
      "slug,name,short-name,avatar-url,title,company-name,is-followed,is-insight-provider,activated";
    filters["fields[companies]"] =
      "slug,name,avatar-url,publish-under-company-name,title,analysts-count,is-followed";
    filters["fields[content-verticals]"] = "slug,name";
    switch (tab) {
      case "insights":
        filters["filter[is-broker]"] = "false";
        filters["filter[is-issuerpaid]"] = "false";
        filters["filter[is-exchange-sponsored]"] = "false";
        break;
      case "broker-reports":
        filters["filter[is-broker]"] = "true";
        break;
      case "sponsored":
        filters["filter[or][is-issuerpaid]"] = "true";
        filters["filter[or][is-exchange-sponsored]"] = "true";
        break;
    }

    if (filterTxt) {
      filters["filter[fulltext]"] = filterTxt;
    }

    return {
      endpoint,
      sort,
      filters,
      page: size,
      pageSize: 5,
    };
  }, [filterTxt, mobile, size, slug, sortOption, tab]);

  const {
    onRequestPrimer,
    requestPrimerLoader,
    isRequestingPrimer,
    isCountingDown,
    showStripeModal,
    toggleStripeModal,
  } = usePrimer({
    entity,
    tab,
    insightSwr: swr,
  });

  const onEmptyStateActionClick = useCallback(async () => {
    router.push(`/entities/${slug}/research`);
  }, [router, slug]);

  useEffect(() => {
    if (filterTxtValue?.length >= 3) {
      setFilterTxt(filterTxtValue);
      setFilterTxtError("");
    } else {
      setFilterTxt("");
      setFilterTxtError("Please enter at least 3 characters");
    }
  }, [filterTxtValue]);

  useEffect(() => {
    if (!hasMore && !page) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      sessionStorage.removeItem("entity-slug");
    } catch (error: any) {
      Bugsnag.notify(error);
    }
  }, [slug]);

  useEffect(() => {
    if (page && totalPages && page > totalPages) {
      router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          page: totalPages,
        },
      });
    }
  }, [page, router, totalPages]);

  const { prettyName: name } = entity || {};
  const firstPublication = data?.[0]?.data?.[0];
  const description = truncate(
    `Independent Research, Discussions, Filings, Analytics, SmartScore, Quant Rating and Latest News on ${name} (${
      entity?.uniqueTicker
    })${firstPublication ? ` - ${firstPublication?.tagline}` : "."}`,
    {
      length: 155,
      separator: /[/\- ]/,
    }
  );

  const snippetStyle = !mobile ? "snippetV2" : "snippet";

  const controller = useMemo(() => {
    if (tab === "primer") {
      return (
        <div
          className={twMerge(
            styles.filter,
            "px-[15px] pb-[15px] md:px-[30px] md:py-[15px] md:flex md:items-center md:justify-between",
            "overflow-auto"
          )}
        >
          <div className="flex items-center">
            <span
              className={twMerge(styles.refresh, "hidden md:block")}
              onClick={() => mutate()}
            >
              Refresh
            </span>
            <div
              className={twMerge(
                styles.filterBox,
                "ml-0 md:ml-[30px] mr-[10px]"
              )}
            >
              <FontAwesomeIcon icon={faSearch} />
              <OverlayTrigger
                placement="bottom"
                overlay={
                  filterTxtError ? <Tooltip>{filterTxtError}</Tooltip> : <></>
                }
              >
                <input
                  value={filterTxtValue}
                  autoComplete="false"
                  autoCapitalize="false"
                  onChange={(e) => setFilterTxtValue(e.target.value)}
                  aria-label="search"
                  placeholder="Search"
                  className="min-w-[200px]"
                />
              </OverlayTrigger>
            </div>
          </div>
          <div className="flex items-center md:w-auto space-x-[10px]">
            <div className="hidden md:block">
              <ViewModeToggle
                onToggle={setCompactView}
                isCompact={isCompact}
                showPager
                setSize={setSize}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={twMerge(
          styles.filter,
          "px-[15px] pb-[15px] md:px-[30px] md:py-[15px] md:flex md:items-center md:justify-between"
        )}
      >
        <div className="flex items-center">
          <span
            className={twMerge(styles.refresh, "hidden md:block")}
            onClick={() => mutate()}
          >
            Refresh
          </span>
          <div
            className={twMerge(styles.filterBox, "ml-0 md:ml-[30px] mr-[10px]")}
          >
            <FontAwesomeIcon icon={faSearch} />
            <OverlayTrigger
              placement="bottom"
              overlay={
                filterTxtError ? <Tooltip>{filterTxtError}</Tooltip> : <></>
              }
            >
              <input
                value={filterTxtValue}
                autoComplete="false"
                autoCapitalize="false"
                onChange={(e) => setFilterTxtValue(e.target.value)}
                aria-label="search"
                placeholder="Search"
              />
            </OverlayTrigger>
          </div>
        </div>
        <div className="flex md:w-auto space-x-[10px]">
          <FocusedSwitch
            checked={focusedOnly}
            onSwitch={setFocusedOnly}
            tooltip={
              <div className="text-center">
                Activate to hide Insights where {name} is not the primary
                subject
              </div>
            }
          />
          <span className={styles.dropdown}>
            <Dropdown>
              <Dropdown.Toggle variant="success" id="dropdown-basic">
                {sortOption.name} <FontAwesomeIcon icon={faCaretDown} />
              </Dropdown.Toggle>

              <Dropdown.Menu className={styles.menu}>
                {sortOptions.map((item) => (
                  <Dropdown.Item
                    className={classNames(
                      styles.item,
                      sortOption.id === item.id && styles.active
                    )}
                    key={item.id}
                    onClick={() => setSortOption(item)}
                  >
                    {item.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </span>
          <div className="hidden md:block">
            <ViewModeToggle
              onToggle={setCompactView}
              isCompact={isCompact}
              showPager
              setSize={setSize}
            />
          </div>
        </div>
      </div>
    );
  }, [
    filterTxtError,
    filterTxtValue,
    focusedOnly,
    isCompact,
    mutate,
    name,
    setSize,
    sortOption.id,
    sortOption.name,
    tab,
  ]);

  const list = useMemo(() => {
    return (
      <div className={styles.list}>
        {isRefreshing ? (
          <Loader />
        ) : (
          <InfiniteScroll
            swr={{ data, mutate, isValidating, size, setSize }}
            isReachingEnd={!hasMore}
            endingIndicator={
              isValidating ? (
                <Loader numberOfItems={5} />
              ) : isEmpty && !requestPrimerLoader ? (
                mobile && !tab ? (
                  <AIWorkflowWidget
                    entity={entity}
                    onRequestPrimer={onRequestPrimer}
                  />
                ) : (
                  <EmptyState
                    type={filterTxt ? "search" : "publication"}
                    subType={tab}
                    query={filterTxt}
                    onActionClick={onEmptyStateActionClick}
                    onClearClick={() => setFilterTxtValue("")}
                    entity={entity}
                  />
                )
              ) : mobile &&
                publications.length < 5 &&
                !tab &&
                !isRequestingPrimer &&
                !isCountingDown ? (
                <AIWorkflowWidget
                  entity={entity}
                  onRequestPrimer={onRequestPrimer}
                />
              ) : page ? (
                <Paginator totalPages={totalPages} />
              ) : requestPrimerLoader ? null : (
                <div className={styles.endOfList}>No more insights</div>
              )
            }
          >
            <>
              {publications?.map?.((publication: any, index: number) => {
                if (index === 1) {
                  return (
                    <div key={publication.id} className="">
                      <PublicationSnippet
                        key={publication.id}
                        publication={publication}
                        isCompact={isCompact}
                        useModal={authenticated}
                        originEntity={entity}
                        origin={origin}
                        swr={swr}
                        style={snippetStyle}
                      />
                      <DianomiAd type="in_list_medium" />
                    </div>
                  );
                }

                if (index === 4) {
                  return (
                    <div key={publication.id} className="">
                      <PublicationSnippet
                        key={publication.id}
                        publication={publication}
                        isCompact={isCompact}
                        useModal={authenticated}
                        originEntity={entity}
                        origin={origin}
                        swr={swr}
                        style={snippetStyle}
                      />
                      <DianomiAd type="in_list" />
                      {mobile &&
                        !tab &&
                        !isRequestingPrimer &&
                        !isCountingDown && (
                          <AIWorkflowWidget
                            entity={entity}
                            onRequestPrimer={onRequestPrimer}
                          />
                        )}
                    </div>
                  );
                }

                return (
                  <PublicationSnippet
                    key={publication.id}
                    publication={publication}
                    isCompact={isCompact}
                    useModal={authenticated}
                    originEntity={entity}
                    origin={origin}
                    swr={swr}
                    style={snippetStyle}
                  />
                );
              })}
            </>
          </InfiniteScroll>
        )}
      </div>
    );
  }, [
    authenticated,
    data,
    entity,
    filterTxt,
    hasMore,
    isCompact,
    isCountingDown,
    isEmpty,
    isRefreshing,
    isRequestingPrimer,
    isValidating,
    mobile,
    mutate,
    onEmptyStateActionClick,
    onRequestPrimer,
    origin,
    page,
    publications,
    requestPrimerLoader,
    setSize,
    size,
    snippetStyle,
    swr,
    tab,
    totalPages,
  ]);

  useEffect(() => {
    if (
      tab === "primer" &&
      router.query["open-primer"] === "true" &&
      !!publications?.length
    ) {
      const slug = publications?.[0]?.slug;
      const link = `/insights/${slug}`;
      router.push(link, undefined, {
        shallow: true,
        scroll: false,
      });
    }
  }, [data, publications, router, tab]);

  return (
    <div className={styles.container}>
      <Head>
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
      </Head>
      {controller}
      {requestPrimerLoader}
      {list}
      {showStripeModal && <AccountUpgradeModal onClose={toggleStripeModal} />}
    </div>
  );
};

export default EntityPageInsightsList;
